import { XIcon } from '@phosphor-icons/react';
import { ErrorBoundary } from '@sentry/react';
import React, { FunctionComponent, useEffect, useContext } from 'react';

import { CompactIconButton } from '@/core/buttons/IconButton';
import { DirtyFormContext } from '@/core/DirtyFormContext';
import { ErrorMessage } from '@/ErrorMessage';
import { translate } from '@/i18n';
import { DrawerComponent } from '@/metronic/components';

import { DrawerContext } from './DrawerContext';

export const DrawerRoot: FunctionComponent = () => {
  const context = useContext(DrawerContext);

  if (!context) {
    return null;
  }

  const { drawerComponent, drawerProps, closeDrawer } = context;
  const [isDirtyContext, setIsDirtyContext] = React.useState(false);
  const isDirtyForm = isDirtyContext;
  const onHide = () => {
    if (
      isDirtyForm &&
      !confirm(
        translate(
          'You have entered data in form. When drawer is closed form data would be lost.',
        ),
      )
    ) {
      return;
    }
    closeDrawer();
  };

  const drawer = DrawerComponent.getInstance('kt_drawer');
  useEffect(() => {
    if (drawer) drawer.update();
  }, [drawerProps, drawer]);

  return (
    <div
      id="kt_drawer"
      className="bg-body"
      data-kt-drawer="true"
      data-kt-drawer-managed="react"
      data-kt-drawer-name="drawer"
      data-kt-drawer-activate="true"
      data-kt-drawer-overlay="true"
      data-kt-drawer-width={`{default:'100%', 'lg': '${drawerProps.width}'}`}
      data-kt-drawer-direction="end"
      data-kt-drawer-toggle="#kt_drawer_toggle"
      data-kt-drawer-close="#kt_drawer_close"
    >
      <div className="card shadow-none rounded-0 w-100">
        <div className="card-header" id="kt_drawer_header">
          <div>
            <h3 className="card-title fw-bolder text-dark fs-3">
              {drawerProps.title}
            </h3>
            {drawerProps.subtitle && (
              <h6 className="text-muted card-subtitle fw-bold">
                {drawerProps.subtitle}
              </h6>
            )}
          </div>

          <div className="card-toolbar gap-2">
            {drawerProps.toolbar ? (
              React.createElement(drawerProps.toolbar, { close: onHide })
            ) : (
              <CompactIconButton
                iconNode={<XIcon weight="bold" />}
                tooltip={translate('Close')}
                onClick={onHide}
                tooltipPlacement="bottom"
              />
            )}
          </div>
        </div>
        <div className="card-body scroll-y p-0" id="kt_drawer_body">
          <DirtyFormContext.Provider value={{ setIsDirty: setIsDirtyContext }}>
            <div className={drawerProps.bodyClassName ?? 'p-8'}>
              <ErrorBoundary fallback={ErrorMessage}>
                {drawerComponent
                  ? React.createElement(drawerComponent, {
                      ...drawerProps,
                      close: onHide,
                    })
                  : null}
              </ErrorBoundary>
            </div>
          </DirtyFormContext.Provider>
        </div>
        {drawerProps.footer && (
          <div className="card-footer py-5 text-center" id="kt_drawer_footer">
            <drawerProps.footer {...drawerProps} close={onHide} />
          </div>
        )}
      </div>
    </div>
  );
};
